import {useEffect, useState} from "react";
import {LogRuns, TicketLogEntry} from "../../interfaces/processing";
import {findLogByTicketId, getAccountLogsRuns, getProcessingLogs, getUnprocessedLogs} from "../../services/processingLogsService";
import {NullString} from "../../interfaces/general";

export interface UseProcessingLogs {
  (
    isCustomerView?: boolean
  ): {
    logs: TicketLogEntry[],
    loadError: NullString,
    loadMoreLogs: () => void,
    showOnlyBugs: boolean,
    toggleLoadOlyBugs: () => void,
    getLogByTicketId: FindLogByTicketId,
    showOnlyAutoTests: boolean,
    toggleShowOnlyAutoTests: () => void,
    showUnprocessedLogs: boolean,
    toggleShowUnprocessedLogs: () => void,
    accountLogsRuns: LogRuns[],
    logsLoading: boolean,
    nextPageLoading: boolean,
    hasMorePages: boolean,
  }
}

export interface FindLogByTicketId {
  (ticketId: string): Promise<{
    success: boolean,
    error: NullString
    log?: TicketLogEntry,
  }>
}

const useProcessingLogs: UseProcessingLogs = (isCustomerView = false) => {
  const [logs, setLogs] = useState<TicketLogEntry[]>([]);
  const [page, setPage] = useState<number>(1);
  const [loadError, setLoadError] = useState<NullString>(null);
  const [showOnlyBugs, setShowOnlyBugs] = useState<boolean>(false);
  const [showOnlyAutoTests, setShowOnlyAutoTests] = useState<boolean>(false);
  const [showUnprocessedLogs, setShowUnprocessedLogs] = useState<boolean>(false);
  const [accountLogsRuns, setAccountLogsRuns] = useState<LogRuns[]>([]);
  const [logsLoading, setLogsLoading] = useState<boolean>(false);
  const [nextPageLoading, setNextPageLoading] = useState<boolean>(false);
  const [hasMorePages, setHasMorePages] = useState<boolean>(false);

  useEffect(() => {
    loadTicketLogs(page, showOnlyBugs, showOnlyAutoTests, false, true);
  }, [])

  async function loadTicketLogs(
    pageNr: number,
    onlyBugs: boolean,
    onlyAutoTests: boolean,
    resetLogs: boolean = false,
    showLoading: boolean = false,
    showNextPageLoading: boolean = false
  ) {
    try {
      setLoadError(null);
      if (showLoading) {
        setLogsLoading(true);
      }
      if (showNextPageLoading) {
        setNextPageLoading(true);
      }

      if (isCustomerView) {
        const logsRes = await getAccountLogsRuns(pageNr, 30);

        if (logsRes.data?.has_more_pages) {
          setHasMorePages(true);
        } else {
          setHasMorePages(false);
        }

        if (Array.isArray(logsRes.data?.logs_runs)) {
          const logsList = [...accountLogsRuns, ...logsRes.data.logs_runs];
          let logIds: string[] = [];
          const uniqueLogs = logsList.filter((log) => {
            if (logIds.includes(log.ticket_log.id)) {
              return false;
            }
            logIds.push(log.ticket_log.id);
            return true;
          });
          setAccountLogsRuns(uniqueLogs);
        } else {
          setLoadError(logsRes.data?.detail || 'An error occurred while loading logs');
        }
        if (logsRes.data?.current_page) {
          setPage(logsRes.data.current_page);
        }
      } else {
        const logsRes = await getProcessingLogs(
          pageNr,
          30,
          onlyBugs,
          onlyAutoTests
        );

        if (Array.isArray(logsRes.data?.logs)) {
          let sortedLogs = logsRes.data.logs;
          if (onlyBugs) {
            sortedLogs = sortedLogs.sort((a: TicketLogEntry, b: TicketLogEntry) => b.bug_severity - a.bug_severity);
          }
  
          if (resetLogs) {
            setLogs(sortedLogs);
          } else {
            setLogs([...logs, ...sortedLogs]);
          }
        } else {
          setLoadError(logsRes.data?.detail || 'An error occurred while loading logs');
        }
      }

      setLogsLoading(false);
      setNextPageLoading(false);
    } catch (err) {
      console.error(err);
    }
  }

  async function toggleLoadOlyBugs() {
    const newShowBool = !showOnlyBugs;
    setShowOnlyBugs(newShowBool);

    loadTicketLogs(page, newShowBool, showOnlyAutoTests, true);
  }

  async function toggleShowOnlyAutoTests() {
    const newShowBool = !showOnlyAutoTests;
    setShowOnlyAutoTests(newShowBool);

    loadTicketLogs(page, showOnlyBugs, newShowBool, true);
  }

  async function loadMoreLogs() {
    const newPage = page + 1;
    setPage(newPage);
    loadTicketLogs(
      newPage,
      showOnlyBugs,
      showOnlyAutoTests,
      false,
      false,
      true
    );
  }

  const getLogByTicketId: FindLogByTicketId = async (ticketId) => {
    try {
      const logRes = await findLogByTicketId(ticketId);

      if (logRes.status === 200) {
        if (logRes.data?.log_entry) {
          return {
            log: logRes.data.log_entry,
            success: true,
            error: null
          }
        } else {
          return {
            success: false,
            error: logRes.data?.detail || 'An error occurred while loading log'
          }
        }
      } else {
        if (logRes.status === 404) {
          return {
            success: true,
            error: 'No ticket found with that ID'
          }
        } else {
          return {
            success: false,
            error: logRes.data?.detail || 'An error occurred while loading log'
          }
        }
      }

    } catch (err: any) {
      console.error(err);

      const errMessage = err?.message || err?.detail || `${err}`;
      return {
        success: false,
        error: errMessage
      }
    }
  };

  const toggleShowUnprocessedLogs = () => {
    const newShowBool = !showUnprocessedLogs;
    setShowUnprocessedLogs(newShowBool);

    if (newShowBool) {
      loadUnprocessedLogs();
    } else {
      loadTicketLogs(page, false, false);
    }
  };

  const loadUnprocessedLogs = async () => {
    const logsRes = await getUnprocessedLogs();
    if (Array.isArray(logsRes.data?.logs)) {
      setLogs(logsRes.data.logs);
    } else {
      setLoadError(logsRes.data?.detail || 'An error occurred while loading logs');
    }
  };

  return {
    logs,
    loadError,
    loadMoreLogs,
    showOnlyBugs,
    toggleLoadOlyBugs,
    getLogByTicketId,
    showOnlyAutoTests,
    toggleShowOnlyAutoTests,
    showUnprocessedLogs,
    toggleShowUnprocessedLogs,
    accountLogsRuns,
    logsLoading,
    nextPageLoading,
    hasMorePages
  }
};

export default useProcessingLogs;
